import { useCallback, useMemo } from "react";
import { useUserContext } from "./userContext";
import { useDesignerAvatar } from "./useDesignerAvatar";
import { useHomeownerAvatar } from "./useHomeownerAvatar";

type UseUserAvatarOptions = {
  autoLoad?: boolean;
};

export const useUserAvatar = (options?: UseUserAvatarOptions) => {
  const { user } = useUserContext();
  const designerAvatar = useDesignerAvatar({ autoLoad: options?.autoLoad });
  const homeownerAvatar = useHomeownerAvatar({ autoLoad: options?.autoLoad });

  const isDesigner = user?.accountType === "Designer";
  const isHomeowner = user?.accountType === "Homeowner";

  const avatarUrl = useMemo(() => {
    if (isDesigner) {
      return designerAvatar.avatarUrl;
    }
    if (isHomeowner) {
      return homeownerAvatar.avatarUrl;
    }
    return undefined;
  }, [designerAvatar.avatarUrl, homeownerAvatar.avatarUrl, isDesigner, isHomeowner]);

  const initials = useMemo(() => {
    if (isDesigner) {
      return designerAvatar.initials;
    }
    if (isHomeowner) {
      return homeownerAvatar.initials;
    }
    return undefined;
  }, [designerAvatar.initials, homeownerAvatar.initials, isDesigner, isHomeowner]);

  const refreshAvatar = useCallback(
    async (forceNetwork: boolean = true) => {
      if (isDesigner) {
        return designerAvatar.refreshAvatar(forceNetwork);
      }
      if (isHomeowner) {
        return homeownerAvatar.refreshAvatar(forceNetwork);
      }
      return undefined;
    },
    [designerAvatar, homeownerAvatar, isDesigner, isHomeowner]
  );

  return { avatarUrl, initials, refreshAvatar };
};
